import {
  ActionSchema,
  ActionType,
  AgentsZapierFacade,
  AppSearchResponse,
  ZapierFacadeError,
} from ".";
import { implementationNeedsAuth } from "./authentications";
import { FILTERED_ACTIONS, FILTERED_APPS } from "./constants";
import { PY2CLI_SELECTED_API_MAPPING } from "./constants";
import { getImplementation } from "./implementations";

export async function searchApps(
  facade: AgentsZapierFacade,
  params: {
    /** Search query to use */
    query: string;

    /** List of apps to filter out. If not provided, default list will be used. */
    filteredApps?: Record<string, boolean>;
  },
): Promise<AppSearchResponse> {
  const { query, filteredApps } = params;

  if (query.length === 0) {
    return {
      results: [],
    };
  }

  const { data, error, response } = await facade.api.GET(
    "/api/v4/implementations-meta/search/",
    {
      params: {
        query: {
          // @ts-expect-error -- for some reason, there are two different types for this endpoint?
          term: query,
        },
      },
    },
  );

  if (error) {
    throw new ZapierFacadeError(
      "/api/v4/implementations-meta/search/",
      "GET",
      params,
      response,
      // NOTE: error.errors here is an Object not an array
      // {
      //   "errors": {
      //     "term": [
      //         "This field may not be blank."
      //     ]
      // }
      // }
      error?.errors,
    );
  }

  const results = data.results ?? [];

  const filteredResults = results.filter((result) => {
    const versionlessApp = result.id.split("@")[0];

    // Filter out legacy apps
    const isLegacyApp = Object.values(PY2CLI_SELECTED_API_MAPPING).includes(
      versionlessApp,
    );

    return !(filteredApps ?? FILTERED_APPS)[versionlessApp] && !isLegacyApp;
  });

  return {
    results: filteredResults.map((searchResults) => ({
      ...searchResults,
      versionless_selected_api: searchResults.id.split("@")[0],
    })),
  };
}

export async function searchActions(
  facade: AgentsZapierFacade,
  params: {
    /** The app to search for actions in */
    selectedApi: string;

    /** Search query to use; if not provided, all actions will be returned */
    query?: string;

    /** Filter to use when searching for actions */
    filterActionType: ActionType[];
  },
): Promise<{ results: ActionSchema[] }> {
  const implementation = await getImplementation(facade, {
    selectedApi: params.selectedApi,
  });

  const filteredActions: ActionSchema[] = [];

  (implementation?.actions || []).forEach((action) => {
    // skip hidden actions
    if (action?.is_hidden) {
      return;
    }

    // some actions are filtered out from Agents completely
    if (
      FILTERED_ACTIONS[implementation.selected_api.split("@")[0]]?.[
        action.key
      ]?.[action.type as ActionType]
    ) {
      return;
    }

    const matchesActionType =
      action?.type &&
      params.filterActionType.includes(action.type as ActionType);

    const matchesQuery =
      !params.query ||
      (action.name &&
        action.name.toLowerCase().includes(params.query.toLowerCase())) ||
      (action.description &&
        action.description.toLowerCase().includes(params.query.toLowerCase()));

    // test against query and action type filter
    if (matchesActionType && matchesQuery) {
      filteredActions.push({
        ...action,
        app_name: implementation.name ?? "",
        display_name: `${implementation.name}: ${action.name}`,
        selected_api: implementation.selected_api ?? "",
        versionless_selected_api:
          implementation.selected_api?.split("@")[0] ?? "",
        app_needs_auth: implementationNeedsAuth(implementation),
      });
    }
  });

  return {
    results: filteredActions,
  };
}
